/**
 * Translates raw Supabase auth error strings into user-actionable copy.
 *
 * v0.8.52 — split the "not allowed" family. Pre-fix one regex
 * (`/not allowed|disabled/`) mapped three different failures to the same
 * "isn't invited yet" copy, so an invitee blocked by a project-level
 * setting looked exactly like an invitee with no invite (founder hit this
 * on the first real household invite). Each mapped message now also ends
 * with the raw server text so a phone screenshot is enough to diagnose.
 */
export function mapAuthError(raw: string): string {
  const details = ` (Details: ${raw})`

  // Our Before-User-Created hook (migration 004): no allowlist row for
  // this exact address.
  if (/not allowed for this email/i.test(raw)) {
    return (
      'This email isn’t invited yet. Ask the household owner for an invite link, and sign in with exactly the address it was sent to — a different spelling or alias won’t match.' +
      details
    )
  }

  // GoTrue's project-level switch: "Signups not allowed for this instance".
  if (/signups? not allowed|signups? (are )?disabled/i.test(raw)) {
    return (
      'New accounts are switched off for this DebtOS project. The owner needs to turn on “Allow new users to sign up” in Supabase (Authentication → Sign In / Providers) and save.' +
      details
    )
  }

  // The hook itself errored (permissions, function missing) rather than
  // returning a decision.
  if (/hook/i.test(raw)) {
    return 'The sign-up check failed on the server — the invite gate is misconfigured.' + details
  }

  if (/rate.?limit|too many requests|60 seconds|over_email_send/i.test(raw)) {
    return 'Email rate limit hit. Supabase’s free tier caps magic-link emails at ~3 per hour per address. Wait an hour, or ask the workspace owner to send a magic link directly from the Supabase dashboard.'
  }
  return raw
}
